import { Alert } from "@/src/lib/alert";
import { useComposerDraft, type ComposerDraftAttachment } from "@/src/hooks/useComposerDraft";
import { supportsMediaPublishing, supportsDurablePublishing } from "@/src/lib/platformCapabilities";
import { WEB_MEDIA_NOTICE } from "@/src/lib/unsupportedMedia";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { Ionicons } from "@/src/components/ui/ionicons";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import { router, useLocalSearchParams } from "expo-router";
import {
  collection,
  doc,
} from "firebase/firestore";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useProfile } from "../../src/hooks/useProfile";
import { useActiveAccount } from "../../src/hooks/useActiveAccount";
import { AccountSwitcher } from "../../src/components/AccountSwitcher";
import {
  createPostUploadError,
  formatAttachmentSize,
  MAX_POST_IMAGE_BYTES,
  MAX_POST_IMAGES,
  pendingImagesFromAssets,
} from "../../src/lib/postAttachments";
import { auth, db } from "../../src/lib/firebase";
import {
  buildPostAuthorAttribution,
  firebaseErrorDetails,
  safePostErrorMessage,
} from "../../src/lib/postPublishing";
import { fetchMemberships } from "../../src/lib/memberships";
import { createIdentityGroup } from "../../src/lib/identityGroups";
import { clearNativeComposerReturn, markNativeComposerReturn } from "../../src/lib/nativeComposerResume";
import {
  appendComposerAttachments,
  classifyImagePickerResult,
  unseenCameraAssets,
} from "../../src/lib/cameraResultRecovery";
import {
  canAuthorPublishDocuments,
  canPublishDocuments,
  isTeacher,
} from "../../src/lib/identity";
import { mapWithConcurrency } from "../../src/lib/postPublishCore";
import { prepareDurableImage, prepareDurablePdf, releasePostPublishAttachment } from "../../src/lib/postPublishFiles";
import { postPublishQueue } from "../../src/lib/postPublishQueue";
import { POST_PUBLISH_SCHEMA_VERSION, type PostPublishAttachment, type PostPublishJob } from "../../src/lib/postPublishTypes";
import { SUBJECTS } from "../../src/lib/taxonomy";
import { contentMaxWidths } from "../../src/theme/layout";

const BG = surface.secondary;
const ACCENT = brand.primary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;
const POST_IMAGE_PREVIEW_SIZE = 128;
const POST_IMAGE_PREVIEW_SPACING = 8;

type Mode = "post" | "group";
type Audience = "global" | "group";

type ComposerAttachment = {
  id: string;
  uri: string;
  name: string;
  status: "preparing" | "ready" | "failed";
  prepared?: PostPublishAttachment;
  error?: string;
};

function localId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

type Membership = {
  groupId: string;
  groupName: string;
  role: "owner" | "member" | "admin";
  isPublic: boolean;
};

function safeGroupName(name?: string, groupId?: string) {
  if (name && name.trim().length > 0) return name;
  return groupId ? "Groupe sans nom" : "Groupe";
}

export default function CreateScreen() {
  const user = auth.currentUser;
  const { profile } = useProfile(user);
  const { activeAccount, activeAccountId, loading: accountsLoading, error: accountsError } = useActiveAccount();

  const params = useLocalSearchParams<{ audience?: string; groupId?: string; resumeExternalActivity?: string }>();
  const preAudience = (params?.audience === "group" ? "group" : null) as
    | Audience
    | null;
  const preGroupId = params?.groupId ? String(params.groupId) : null;

  const subjects = SUBJECTS;

  const [mode, setMode] = useState<Mode>("post");
  const [postSubject, setPostSubject] = useState<string>(subjects[0]?.value ?? "general");
  const [postText, setPostText] = useState("");
  const [audience, setAudience] = useState<Audience>("global");
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [composerJobId, setComposerJobId] = useState(() => localId("job"));
  const [composerPostId, setComposerPostId] = useState(() => doc(collection(db, "posts")).id);
  const [images, setImages] = useState<ComposerAttachment[]>([]);
  const [pdfAttachment, setPdfAttachment] = useState<ComposerAttachment | null>(null);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupDesc, setGroupDesc] = useState("");
  const [groupSubject, setGroupSubject] = useState<string>("general");
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const handledPendingPickerRef = useRef(false);
  const seenCameraAssetKeysRef = useRef(new Set<string>());
  const isPublishingRef = useRef(false);
  const draftSubjectRestoredRef = useRef(false);
  const subjectManuallySelectedRef = useRef(false);
  const imagesRef = useRef<ComposerAttachment[]>([]);
  const pdfAttachmentRef = useRef<ComposerAttachment | null>(null);

  useEffect(() => {
    pdfAttachmentRef.current = pdfAttachment;
  }, [pdfAttachment]);

  useEffect(() => () => {
    if (supportsDurablePublishing) return;
    imagesRef.current.forEach((image) => releasePostPublishAttachment(image.prepared));
    releasePostPublishAttachment(pdfAttachmentRef.current?.prepared);
  }, []);

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!user || !activeAccountId) {
        setMemberships([]);
        return;
      }

      try {
        const cached = await fetchMemberships(activeAccountId, 100);
        if (!alive) return;
        const list: Membership[] = cached;

        setMemberships(list);

        if (preAudience === "group") {
          setAudience("group");
          if (preGroupId) setSelectedGroupId(preGroupId);
        } else if (list.length > 0) {
          setSelectedGroupId((current) => current ?? list[0].groupId);
        }
      } catch {
        if (!alive) return;
        setMemberships([]);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [activeAccountId, preAudience, preGroupId, user]);

  useEffect(() => {
    if (audience === "group" && !selectedGroupId && memberships.length > 0) {
      setSelectedGroupId(memberships[0].groupId);
    }
  }, [audience, memberships, selectedGroupId]);

  const persistedImages: ComposerDraftAttachment[] = images.flatMap((image) => (
    image.status === "ready" && image.prepared
      ? [{ id: image.id, uri: image.uri, name: image.name, status: "ready" as const, prepared: image.prepared }]
      : []
  ));
  const persistedPdf: ComposerDraftAttachment | null = pdfAttachment?.status === "ready" && pdfAttachment.prepared
    ? {
        id: pdfAttachment.id,
        uri: pdfAttachment.uri,
        name: pdfAttachment.name,
        status: "ready",
        prepared: pdfAttachment.prepared,
      }
    : null;
  const composerDraftState = useComposerDraft(user?.uid, {
    text: postText,
    subject: postSubject,
    audience,
    groupId: selectedGroupId,
    jobId: composerJobId,
    postId: composerPostId,
    images: persistedImages,
    pdfAttachment: persistedPdf,
  }, (draft) => {
    draftSubjectRestoredRef.current = true;
    setPostText(draft.text);
    setPostSubject(draft.subject);
    setAudience(draft.audience);
    setSelectedGroupId(draft.groupId);
    setComposerJobId(draft.jobId);
    setComposerPostId(draft.postId);
    const restoredImages = (draft.images ?? []) as ComposerAttachment[];
    imagesRef.current = restoredImages;
    setImages(restoredImages);
    const restoredPdf = (draft.pdfAttachment ?? null) as ComposerAttachment | null;
    pdfAttachmentRef.current = restoredPdf;
    setPdfAttachment(restoredPdf);
  });

  useEffect(() => {
    if (!composerDraftState.loaded || draftSubjectRestoredRef.current || subjectManuallySelectedRef.current) return;
    if (activeAccount?.accountType === "teacher" && activeAccount.mainSubject) {
      setPostSubject(activeAccount.mainSubject);
      return;
    }
    if (!activeAccount && profile && isTeacher(profile) && profile.mainSubject) {
      setPostSubject(profile.mainSubject);
    }
  }, [activeAccount, composerDraftState.loaded, profile]);

  async function beginExternalComposerActivity(kind: "image" | "camera" | "document") {
    if (Platform.OS !== "android" || !user) return true;
    if (!await composerDraftState.saveNow()) {
      Alert.alert("Brouillon non sauvegardé", "Impossible de sauvegarder ce brouillon sur cet appareil.");
      return false;
    }
    try {
      await markNativeComposerReturn(user.uid, kind);
      return true;
    } catch {
      Alert.alert("Reprise indisponible", "Impossible de préparer le retour au brouillon.");
      return false;
    }
  }

  async function finishExternalComposerActivity() {
    if (Platform.OS !== "android" || !user) return;
    try {
      await clearNativeComposerReturn(user.uid);
    } catch {
      if (__DEV__) console.warn("could not clear composer return marker");
    }
  }

  const appendPickedImages = useCallback(
    async (assets: ImagePicker.ImagePickerAsset[], source: "camera" | "library" = "library") => {
      const selectedAssets = source === "camera"
        ? unseenCameraAssets(assets, seenCameraAssetKeysRef.current)
        : assets;
      if (selectedAssets.length === 0) return;

      const remainingSlots = MAX_POST_IMAGES - imagesRef.current.length;
      if (remainingSlots <= 0) {
        Alert.alert("Limite atteinte", `Tu peux joindre jusqu'à ${MAX_POST_IMAGES} photos.`);
        return;
      }

      const { attachments, rejectedTooLarge, rejectedInvalid } = pendingImagesFromAssets(
        selectedAssets,
        remainingSlots
      );

      if (rejectedTooLarge > 0) {
        Alert.alert(
          "Photo trop lourde",
          `Chaque photo doit faire moins de ${formatAttachmentSize(MAX_POST_IMAGE_BYTES)}.`
        );
      }
      if (rejectedInvalid > 0) {
        Alert.alert("Format invalide", "Certaines photos n'ont pas pu etre lues.");
      }

      if (attachments.length === 0) return;

      const drafts = attachments.map((attachment) => ({
        id: localId("image"),
        uri: attachment.uri,
        name: attachment.name ?? "photo.jpg",
        status: "preparing" as const,
        source: attachment,
      }));
      setImages((prev) => {
        const next = appendComposerAttachments(prev, drafts, MAX_POST_IMAGES);
        imagesRef.current = next;
        return next;
      });
      await mapWithConcurrency({
        items: drafts,
        concurrency: 2,
        worker: async (draft) => {
          try {
            const prepared = await prepareDurableImage({
              jobId: composerJobId,
              postId: composerPostId,
              attachmentId: draft.id,
              sourceUri: draft.source.uri,
              name: draft.source.name,
              size: draft.source.size,
              width: draft.source.width,
              height: draft.source.height,
            });
            setImages((prev) => {
              const next = prev.map((item) => item.id === draft.id
                ? { ...item, uri: prepared.previewUri, status: "ready" as const, prepared }
                : item);
              imagesRef.current = next;
              return next;
            });
          } catch (error) {
            setImages((prev) => {
              const next = prev.map((item) => item.id === draft.id
                ? { ...item, status: "failed" as const, error: safePostErrorMessage(error, "Photo illisible.") }
                : item);
              imagesRef.current = next;
              return next;
            });
          }
        },
      });
    },
    [composerJobId, composerPostId]
  );

  useEffect(() => {
    if (Platform.OS !== "android" || handledPendingPickerRef.current) return;
    if (!composerDraftState.loaded) return;
    if (params.resumeExternalActivity !== "camera" && params.resumeExternalActivity !== "image") return;

    handledPendingPickerRef.current = true;
    let alive = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryIndex = 0;
    let pendingReadFailed = false;
    const retryDelays = [350, 700, 1200];

    const showRecoveryError = () => Alert.alert(
      "Reprise impossible",
      "La photo récupérée n’a pas pu être lue. Ton brouillon reste sauvegardé."
    );

    const retryOrFinish = () => {
      if (!alive) return;
      if (retryIndex < retryDelays.length) {
        retryTimer = setTimeout(() => { void checkPendingResult(); }, retryDelays[retryIndex]);
        retryIndex += 1;
      } else if (pendingReadFailed) {
        showRecoveryError();
      }
    };

    async function checkPendingResult() {
      try {
        const pendingResult = await ImagePicker.getPendingResultAsync();
        if (!alive) return;
        const recovery = classifyImagePickerResult(pendingResult);
        if (recovery.kind === "empty") {
          retryOrFinish();
          return;
        }
        if (recovery.kind === "cancelled") return;
        if (recovery.kind === "error" || recovery.kind === "invalid") {
          showRecoveryError();
          return;
        }

        try {
          await appendPickedImages(
            recovery.assets,
            params.resumeExternalActivity === "camera" ? "camera" : "library"
          );
        } catch {
          showRecoveryError();
        }
      } catch {
        pendingReadFailed = true;
        if (__DEV__) console.warn("pending image picker result unavailable");
        retryOrFinish();
      }
    }

    void checkPendingResult();

    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [appendPickedImages, composerDraftState.loaded, params.resumeExternalActivity]);

  async function pickImages() {
    if (!supportsMediaPublishing) return;
    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert("Permission requise", "Autorise l'accès à la galerie photo.");
        return;
      }

      const remainingSlots = MAX_POST_IMAGES - images.length;
      if (remainingSlots <= 0) {
        Alert.alert("Limite atteinte", `Tu peux joindre jusqu'à ${MAX_POST_IMAGES} photos.`);
        return;
      }

      if (!await beginExternalComposerActivity("image")) return;

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        selectionLimit: remainingSlots,
        quality: 1,
        exif: false,
      });

      if (result.canceled || !result.assets?.length) return;

      await appendPickedImages(result.assets);
    } catch (e: unknown) {
      const error = createPostUploadError(
        "post/image-picker-failed",
        "Impossible d'ajouter cette photo.",
        e
      );
      console.error("upload:image:error", {
        ...firebaseErrorDetails(error),
        stage: "image-picker",
      });
      Alert.alert("Erreur", safePostErrorMessage(error, "Impossible d’ajouter cette photo."));
    } finally {
      await finishExternalComposerActivity();
    }
  }

  async function takePhoto() {
    if (!supportsMediaPublishing) return;
    try {
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      if (!permission.granted) {
        Alert.alert("Permission requise", "Autorise l'accès à l'appareil photo.");
        return;
      }
      if (!await beginExternalComposerActivity("camera")) return;
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"],
        quality: 1,
        exif: false,
      });
      const recovery = classifyImagePickerResult(result);
      if (recovery.kind === "success") await appendPickedImages(recovery.assets, "camera");
      else if (recovery.kind === "error" || recovery.kind === "invalid") {
        Alert.alert("Erreur", "La photo prise n’a pas pu être lue. Ton brouillon reste sauvegardé.");
      }
    } catch (error) {
      Alert.alert("Erreur", safePostErrorMessage(error, "Impossible de prendre la photo."));
    } finally {
      await finishExternalComposerActivity();
    }
  }

  async function pickPdf() {
    if (!supportsMediaPublishing) return;
    if (!canPublishDocuments(activeAccount)) {
      Alert.alert("Indisponible", "Seuls les professeurs et les institutions peuvent joindre des PDF.");
      return;
    }

    if (!await beginExternalComposerActivity("document")) return;
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: "application/pdf",
        copyToCacheDirectory: true,
        multiple: false,
      });

      if (result.canceled || !result.assets?.length) return;

      const asset = result.assets[0];
      const id = localId("pdf");
      const name = asset.name ?? `document_${Date.now()}.pdf`;
      releasePostPublishAttachment(pdfAttachmentRef.current?.prepared);
      setPdfAttachment({ id, uri: asset.uri, name, status: "preparing" });
      try {
        const prepared = await prepareDurablePdf({
          jobId: composerJobId,
          attachmentId: id,
          sourceUri: asset.uri,
          name,
          size: asset.size,
        });
        setPdfAttachment((current) => current?.id === id
          ? { ...current, uri: prepared.previewUri, status: "ready", prepared }
          : current);
      } catch (error) {
        setPdfAttachment((current) => current?.id === id
          ? { ...current, status: "failed", error: safePostErrorMessage(error, "PDF illisible.") }
          : current);
      }
    } catch (error) {
      Alert.alert("Erreur", safePostErrorMessage(error, "Impossible d’ajouter ce PDF."));
    } finally {
      await finishExternalComposerActivity();
    }
  }

  function removeImageAt(index: number) {
    setImages((prev) => {
      releasePostPublishAttachment(prev[index]?.prepared);
      const next = prev.filter((_, currentIndex) => currentIndex !== index);
      imagesRef.current = next;
      return next;
    });
  }

  function clearPdfAttachment() {
    releasePostPublishAttachment(pdfAttachmentRef.current?.prepared);
    setPdfAttachment(null);
  }

  async function onPublishPost() {
    if (isPublishingRef.current) {
      if (__DEV__) console.info("publish:blocked-already-publishing");
      return;
    }
    isPublishingRef.current = true;
    setIsPublishing(true);

    const selectedImages = imagesRef.current;
    const finishPublish = () => {
      isPublishingRef.current = false;
      setIsPublishing(false);
    };

    if (!user) {
      finishPublish();
      Alert.alert("Erreur", "Session manquante.");
      return;
    }

    let canonicalAuthor;
    try {
      if (!activeAccount) {
        throw new Error(accountsLoading
          ? "Les comptes publics sont encore en cours de chargement."
          : "Aucun compte public actif n'est disponible.");
      }
      canonicalAuthor = buildPostAuthorAttribution(user.uid, activeAccount);
    } catch (error) {
      finishPublish();
      console.error("publish:profile:error", firebaseErrorDetails(error));
      Alert.alert("Profil à actualiser", safePostErrorMessage(error, "Profil indisponible. Réessaie."));
      return;
    }

    const cleanText = postText.trim();
    if (!cleanText && selectedImages.length === 0 && !pdfAttachment) {
      finishPublish();
      Alert.alert("Erreur", "Écris quelque chose ou ajoute au moins un fichier.");
      return;
    }

    if (cleanText.length > 3000) {
      finishPublish();
      Alert.alert("Erreur", "Le post est trop long.");
      return;
    }

    if (pdfAttachment && !canAuthorPublishDocuments(canonicalAuthor)) {
      finishPublish();
      Alert.alert("Erreur", "Seuls les professeurs et les institutions peuvent joindre des PDF.");
      return;
    }

    if (audience === "group") {
      if (!selectedGroupId) {
        finishPublish();
        Alert.alert("Erreur", "Choisis un groupe.");
        return;
      }
      const ok = memberships.some((m) => m.groupId === selectedGroupId);
      if (!ok) {
        finishPublish();
        Alert.alert("Erreur", "Tu ne fais pas partie de ce groupe.");
        return;
      }
    }

    try {
      setBusy(true);
      const notReady = selectedImages.some((image) => image.status !== "ready" || !image.prepared)
        || (!!pdfAttachment && (pdfAttachment.status !== "ready" || !pdfAttachment.prepared));
      if (notReady) {
        throw Object.assign(new Error("Attends la fin de la préparation des fichiers."), { code: "post/preparing" });
      }

      const selectedGroup = audience === "group"
        ? memberships.find((membership) => membership.groupId === selectedGroupId) ?? null
        : null;
      if (audience === "group" && !selectedGroup) throw new Error("Choisis un groupe valide.");
      const postId = composerPostId;
      const attachments = [
        ...selectedImages.map((image) => image.prepared!),
        ...(pdfAttachment?.prepared ? [pdfAttachment.prepared] : []),
      ].map((attachment, order) => ({ ...attachment, order }));
      const now = Date.now();
      const job: PostPublishJob = {
        schemaVersion: POST_PUBLISH_SCHEMA_VERSION,
        jobId: composerJobId,
        postId,
        authorUid: user.uid,
        createdAtLocal: now,
        updatedAtLocal: now,
        status: "queued",
        attemptCount: 0,
        text: cleanText,
        subject: postSubject,
        ...(activeAccount?.accountType === "student" && activeAccount.track
          ? { track: activeAccount.track }
          : {}),
        visibility: audience,
        groupId: selectedGroup?.groupId ?? null,
        group: selectedGroup ? {
          groupId: selectedGroup.groupId,
          groupName: selectedGroup.groupName,
          isPublic: selectedGroup.isPublic,
        } : null,
        author: canonicalAuthor,
        attachments,
        progress: { completed: 0, total: attachments.length, fraction: attachments.length ? 0 : 1 },
      };
      await postPublishQueue.enqueue(job);
      // Clear before the success dialog navigates away, not in an effect that
      // could be skipped when the composer unmounts.
      const draftCleared = await composerDraftState.clearNow();
      if (!draftCleared && __DEV__) console.warn("publish:composer-draft-clear-failed");

      setPostText("");
      setImages([]);
      imagesRef.current = [];
      setPdfAttachment(null);
      setAudience("global");
      setSelectedGroupId(null);
      draftSubjectRestoredRef.current = false;
      subjectManuallySelectedRef.current = false;
      setPostSubject(activeAccount?.accountType === "teacher" && activeAccount.mainSubject
        ? activeAccount.mainSubject
        : !activeAccount && profile && isTeacher(profile) && profile.mainSubject
          ? profile.mainSubject
          : subjects[0]?.value ?? "general");
      setComposerJobId(localId("job"));
      setComposerPostId(doc(collection(db, "posts")).id);
      Alert.alert(
        "Publication enregistrée",
        "Ta publication est en attente de validation. Ne la republie pas.",
        [{ text: "Voir le feed", onPress: () => router.replace("/(tabs)") }],
        { cancelable: false }
      );
    } catch (e: unknown) {
      const errorDetails = firebaseErrorDetails(e);
      if (__DEV__) {
        console.error("publish:error", {
          code: errorDetails.code,
          stage: "persist-job",
          jobId: composerJobId.slice(-8),
        });
      }
      Alert.alert(
        "Erreur",
        safePostErrorMessage(e, supportsDurablePublishing ? "Publication impossible. Réessaie dans un instant." : "Envoi non confirmé. Vérifie tes publications avant de réessayer.")
      );
    } finally {
      setBusy(false);
      finishPublish();
    }
  }

  async function onCreateGroup() {
    if (!user || !profile) {
      Alert.alert("Erreur", "Profil ou session manquants.");
      return;
    }

    const name = groupName.trim();
    const description = groupDesc.trim();

    if (name.length < 3) return Alert.alert("Erreur", "Le nom du groupe doit faire au moins 3 caractères.");
    if (name.length > 60) return Alert.alert("Erreur", "Le nom du groupe est trop long.");
    if (description.length > 220) return Alert.alert("Erreur", "La description est trop longue.");

    if (!activeAccountId || !activeAccount) {
      Alert.alert("Erreur", "Choisis une identité active avant de créer un groupe.");
      return;
    }
    const subjectNormalized = groupSubject;

    try {
      setBusy(true);

      const { groupId } = await createIdentityGroup({
        identityId: activeAccountId,
        name,
        description,
        subject: subjectNormalized,
        isPublic,
      });

      setGroupName("");
      setGroupDesc("");
      setGroupSubject("general");
      setIsPublic(true);

      setMemberships((prev) => {
        const next: Membership[] = [
          { groupId, groupName: name, role: "owner", isPublic },
          ...prev.filter((item) => item.groupId !== groupId),
        ];
        next.sort((a, b) => (a.groupName ?? "").localeCompare(b.groupName ?? ""));
        return next;
      });

      setSelectedGroupId(groupId);
      router.back();
    } catch (e: any) {
      if (__DEV__) console.warn("create group failed", e?.code ?? "unknown");
      Alert.alert("Création impossible", "Le groupe n'a pas pu être créé. Vérifie ta connexion et réessaie.");
    } finally {
      setBusy(false);
    }
  }

  function onPressPublish() {
    if (isPublishingRef.current || busy) {
      if (__DEV__) console.info("publish:blocked-already-publishing");
      return;
    }

    void onPublishPost();
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "left", "right"]}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === "ios" ? "padding" : undefined}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          alignSelf: "center",
          boxSizing: "border-box",
          maxWidth: contentMaxWidths.create,
          width: "100%",
          paddingHorizontal: 16,
          paddingTop: 12,
          paddingBottom: 32,
          gap: 10,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
        keyboardShouldPersistTaps="handled"
      >
        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
          <Text style={{ color: uiText.primary, fontSize: 26, fontWeight: "900" }}>
            {mode === "post" ? "Nouveau post" : "Nouveau groupe"}
          </Text>
          {mode === "post" ? (
            <Pressable accessibilityRole="button" accessibilityLabel="Publier la publication" accessibilityState={{ disabled: isPublishing || busy, busy: isPublishing }} onPress={onPressPublish} disabled={isPublishing || busy} style={{ minHeight: 48, justifyContent: "center", paddingVertical: 9, paddingHorizontal: 16, borderRadius: 12, backgroundColor: ACCENT, opacity: isPublishing || busy ? 0.6 : 1 }}>
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>{isPublishing ? (images.length || pdfAttachment ? "Envoi des médias…" : "Publication…") : "Publier"}</Text>
            </Pressable>
          ) : null}
        </View>

        <View style={{ flexDirection: "row", gap: 10 }}>
          <Pressable
            accessibilityRole="radio"
            accessibilityLabel="Publier une publication"
            accessibilityState={{ checked: mode === "post" }}
            onPress={() => setMode("post")}
            style={{
              minHeight: 44,
              paddingHorizontal: 14,
              paddingVertical: 7,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: mode === "post" ? ACCENT : BORDER,
              backgroundColor: mode === "post" ? surface.selected : CARD,
              alignItems: "center",
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>{mode === "post" ? "✓ " : ""}Publication</Text>
          </Pressable>

          <Pressable
            accessibilityRole="radio"
            accessibilityLabel="Créer un groupe"
            accessibilityState={{ checked: mode === "group" }}
            onPress={() => setMode("group")}
            style={{
              minHeight: 44,
              paddingHorizontal: 14,
              paddingVertical: 7,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: mode === "group" ? ACCENT : BORDER,
              backgroundColor: mode === "group" ? surface.selected : CARD,
              alignItems: "center",
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>
              {mode === "group" ? "✓ " : ""}Créer un groupe
            </Text>
          </Pressable>
        </View>

        <View
          style={{
            backgroundColor: CARD,
            borderRadius: 18,
            padding: 14,
            borderWidth: 1,
            borderColor: BORDER,
            gap: 12,
          }}
        >
          {mode === "post" ? (
            <>
              <AccountSwitcher
                compact
                label="Publication au nom de"
                description={accountsError ? "Identité indisponible. Actualise tes comptes." : undefined}
              />
              <BrandTextInput
                key={composerJobId}
                placeholder="Qu'est-ce que tu veux partager ?"
                placeholderTextColor={uiText.muted}
                multiline
                value={postText}
                editable={supportsDurablePublishing || !isPublishing}
                onChangeText={setPostText}
                style={{ color: uiText.primary, backgroundColor: surface.overlay, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 12, minHeight: 96, textAlignVertical: "top" }}
              />
              <Text style={{ color: MUTED, fontWeight: "700" }}>Publier dans</Text>

              <View style={{ flexDirection: "row", gap: 10 }}>
                <Pressable
                  accessibilityRole="radio"
                  accessibilityLabel="Publier dans le fil général"
                  accessibilityState={{ checked: audience === "global" }}
                  onPress={() => setAudience("global")}
                  style={{
                    flex: 1,
                    minHeight: 44,
                    paddingVertical: 10,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: audience === "global" ? ACCENT : BORDER,
                    backgroundColor:
                      audience === "global"
                        ? surface.selected
                        : surface.primary,
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900" }}>{audience === "global" ? "✓ " : ""}Fil général</Text>
                </Pressable>

                <Pressable
                  accessibilityRole="radio"
                  accessibilityLabel="Publier dans un groupe"
                  accessibilityState={{ checked: audience === "group" }}
                  onPress={() => setAudience("group")}
                  disabled={memberships.length === 0}
                  style={{
                    flex: 1,
                    minHeight: 44,
                    paddingVertical: 10,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: audience === "group" ? ACCENT : BORDER,
                    backgroundColor:
                      audience === "group"
                        ? surface.selected
                        : surface.primary,
                    alignItems: "center",
                    opacity: memberships.length === 0 ? 0.5 : 1,
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900" }}>Un groupe</Text>
                </Pressable>
              </View>

              {audience === "group" && (
                <>
                  <Text style={{ color: MUTED, fontWeight: "700" }}>Choisis le groupe</Text>
                  <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
                    {memberships.map((m) => {
                      const active = selectedGroupId === m.groupId;
                      return (
                        <Pressable
                          key={m.groupId}
                          onPress={() => setSelectedGroupId(m.groupId)}
                          accessibilityRole="radio"
                          accessibilityLabel={`Publier dans ${safeGroupName(m.groupName, m.groupId)}`}
                          accessibilityState={{ checked: selectedGroupId === m.groupId }}
                          style={{
                            minHeight: 40,
                            paddingVertical: 8,
                            paddingHorizontal: 12,
                            borderRadius: 999,
                            borderWidth: 1,
                            borderColor: active ? ACCENT : surface.elevated,
                            backgroundColor: active ? ACCENT : surface.primary,
                            maxWidth: "100%",
                          }}
                        >
                          <Text style={{ color: uiText.primary, fontWeight: "700", flexShrink: 1 }}>
                            {safeGroupName(m.groupName, m.groupId)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </>
              )}

              <View style={{ gap: 6 }}>
                <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <Text style={{ color: MUTED, fontWeight: "700" }}>Matière</Text>
                </View>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 10, paddingRight: 16 }}
                  accessibilityLabel="Sélection de matière"
                >
                  {subjects.map((s) => {
                    const active = postSubject === s.value;

                    return (
                      <Pressable
                        key={s.value}
                        accessibilityRole="radio"
                        accessibilityLabel={`Matière : ${s.label}`}
                        accessibilityHint="Les matières défilent horizontalement"
                        accessibilityState={{ checked: active }}
                        onPress={() => {
                          subjectManuallySelectedRef.current = true;
                          setPostSubject(s.value);
                        }}
                        style={{
                          minHeight: 40,
                          paddingVertical: 8,
                          paddingHorizontal: 12,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: active ? ACCENT : surface.elevated,
                          backgroundColor: active ? surface.selected : surface.primary,
                        }}
                      >
                        <Text style={{ color: uiText.primary, fontWeight: "700" }}>
                          {active ? "✓ " : ""}{s.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>

              {supportsMediaPublishing ? (
                <Pressable
                  onPress={() => setAttachmentsExpanded((current) => !current)}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: attachmentsExpanded }}
                  accessibilityLabel={attachmentsExpanded ? "Masquer les pièces jointes" : "Ajouter une pièce jointe"}
                  style={{
                    alignSelf: "flex-start",
                    minHeight: 44,
                    paddingHorizontal: 12,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: BORDER,
                    backgroundColor: surface.primary,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Ionicons name="add" size={21} color={uiText.primary} />
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>Ajouter un média</Text>
                </Pressable>
              ) : (
                <Text accessibilityLiveRegion="polite" style={{ color: MUTED }}>{WEB_MEDIA_NOTICE}</Text>
              )}
              {composerDraftState.error ? <Text accessibilityRole="alert" style={{ color: uiText.danger }}>{composerDraftState.error}</Text> : null}
              {supportsMediaPublishing && attachmentsExpanded ? (
                <View style={{ flexDirection: "row", gap: 10 }}>
                  <Pressable
                    onPress={pickImages}
                    accessibilityRole="button"
                    accessibilityLabel="Choisir des photos"
                    style={{
                      flex: 1,
                      minHeight: 48,
                      borderRadius: 14,
                      borderWidth: 1,
                      borderColor: BORDER,
                      backgroundColor: surface.primary,
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "row",
                      gap: 7,
                    }}
                  >
                    <Ionicons name="images-outline" size={20} color={uiText.primary} />
                    <Text style={{ color: uiText.primary, fontWeight: "800" }}>Photos</Text>
                  </Pressable>

                  {Platform.OS !== "web" ? (
                    <Pressable
                      onPress={takePhoto}
                      accessibilityRole="button"
                      accessibilityLabel="Prendre une photo"
                      style={{ flex: 1, minHeight: 48, borderRadius: 14, borderWidth: 1, borderColor: BORDER, backgroundColor: surface.primary, alignItems: "center", justifyContent: "center", flexDirection: "row", gap: 7 }}
                    >
                      <Ionicons name="camera-outline" size={20} color={uiText.primary} />
                      <Text style={{ color: uiText.primary, fontWeight: "800" }}>Appareil</Text>
                    </Pressable>
                  ) : null}

                  {canPublishDocuments(activeAccount) ? (
                    <Pressable
                      onPress={pickPdf}
                      accessibilityRole="button"
                      accessibilityLabel="Ajouter un PDF"
                      style={{
                        flex: 1,
                        minHeight: 48,
                        borderRadius: 14,
                        borderWidth: 1,
                        borderColor: BORDER,
                        backgroundColor: surface.primary,
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "row",
                        gap: 7,
                      }}
                    >
                      <Ionicons name="document-text-outline" size={20} color={uiText.primary} />
                      <Text style={{ color: uiText.primary, fontWeight: "800" }}>PDF</Text>
                    </Pressable>
                  ) : null}
                </View>
              ) : null}

              {images.length > 0 ? (
                <View
                  style={{
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor: BORDER,
                    backgroundColor: surface.overlay,
                    padding: 12,
                    gap: 10,
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                      {images.length} photo{images.length > 1 ? "s" : ""} sélectionnée{images.length > 1 ? "s" : ""}
                    </Text>
                    <Text style={{ color: MUTED }}>
                      {images.length}/{MAX_POST_IMAGES}
                    </Text>
                  </View>

                  <ScrollView
                    horizontal
                    nestedScrollEnabled
                    snapToInterval={POST_IMAGE_PREVIEW_SIZE + POST_IMAGE_PREVIEW_SPACING}
                    decelerationRate="fast"
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{
                      gap: POST_IMAGE_PREVIEW_SPACING,
                      paddingRight: POST_IMAGE_PREVIEW_SPACING,
                    }}
                  >
                    {images.map((image, index) => (
                      <View
                        key={`${image.uri}_${index}`}
                        style={{ width: POST_IMAGE_PREVIEW_SIZE, gap: POST_IMAGE_PREVIEW_SPACING }}
                      >
                        <Image
                          source={{ uri: image.uri }}
                          style={{
                            width: POST_IMAGE_PREVIEW_SIZE,
                            height: POST_IMAGE_PREVIEW_SIZE,
                            borderRadius: 12,
                            backgroundColor: surface.primary,
                          }}
                          resizeMode="cover"
                        />
                        <Text style={{ color: image.status === "failed" ? uiText.danger : MUTED, fontSize: 12 }}>
                          {image.status === "preparing" ? "Préparation…" : image.status === "failed" ? image.error : "Prête"}
                        </Text>
                        <Pressable
                          onPress={() => removeImageAt(index)}
                          accessibilityRole="button"
                          accessibilityLabel={`Retirer la photo ${index + 1}`}
                          style={{
                            alignSelf: "flex-start",
                            minHeight: 44,
                            paddingVertical: 6,
                            paddingHorizontal: 10,
                            borderRadius: 999,
                            backgroundColor: surface.danger,
                            borderWidth: 1,
                            borderColor: border.danger,
                          }}
                        >
                          <Text style={{ color: uiText.danger, fontWeight: "800" }}>Retirer</Text>
                        </Pressable>
                      </View>
                    ))}
                  </ScrollView>
                </View>
              ) : null}

              {pdfAttachment ? (
                <View
                  style={{
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor: BORDER,
                    backgroundColor: surface.overlay,
                    padding: 12,
                    gap: 10,
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: uiText.primary, fontWeight: "800" }}>PDF sélectionné</Text>
                      <Text style={{ color: MUTED, marginTop: 4 }} numberOfLines={1}>
                        {pdfAttachment.name || pdfAttachment.uri}
                      </Text>
                    </View>

                    <Pressable
                      onPress={clearPdfAttachment}
                      accessibilityRole="button"
                      accessibilityLabel="Retirer le PDF"
                      style={{ minHeight: 44, justifyContent: "center" }}
                    >
                      <Text style={{ color: uiText.danger, fontWeight: "800" }}>Retirer</Text>
                    </Pressable>
                  </View>
                </View>
              ) : null}

            </>
          ) : (
            <>
              <Text style={{ color: MUTED, fontWeight: "700" }}>Nom du groupe</Text>
              <BrandTextInput
                placeholder="Ex: Math Uni"
                placeholderTextColor={uiText.muted}
                value={groupName}
                onChangeText={setGroupName}
                style={{
                  color: uiText.primary,
                  backgroundColor: surface.overlay,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 14,
                  padding: 12,
                }}
              />

              <Text style={{ color: MUTED, fontWeight: "700" }}>Description</Text>
              <BrandTextInput
                placeholder="Objectif du groupe…"
                placeholderTextColor={uiText.muted}
                value={groupDesc}
                onChangeText={setGroupDesc}
                multiline
                style={{
                  color: uiText.primary,
                  backgroundColor: surface.overlay,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 14,
                  padding: 12,
                  minHeight: 90,
                  textAlignVertical: "top",
                }}
              />

              <View style={{ gap: 6 }}>
                <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <Text style={{ color: MUTED, fontWeight: "700" }}>Matière</Text>
                </View>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 10, paddingRight: 16 }}
                  accessibilityLabel="Sélection de matière du groupe"
                >
                  {subjects.map((s) => {
                    const active = groupSubject === s.value;

                    return (
                      <Pressable
                        key={s.value}
                        accessibilityRole="radio"
                        accessibilityLabel={`Matière : ${s.label}`}
                        accessibilityHint="Les matières défilent horizontalement"
                        accessibilityState={{ checked: active }}
                        onPress={() => setGroupSubject(s.value)}
                        style={{
                          minHeight: 40,
                          paddingVertical: 8,
                          paddingHorizontal: 12,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: active ? ACCENT : surface.elevated,
                          backgroundColor: active ? surface.selected : surface.primary,
                        }}
                      >
                        <Text style={{ color: uiText.primary, fontWeight: "700" }}>
                          {active ? "✓ " : ""}{s.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>

              <Text style={{ color: MUTED, fontWeight: "700" }}>Visibilité</Text>
              <View style={{ flexDirection: "row", gap: 10 }}>
                <Pressable
                  accessibilityRole="radio"
                  accessibilityLabel="Groupe public"
                  accessibilityState={{ checked: isPublic }}
                  onPress={() => setIsPublic(true)}
                  style={{
                    flex: 1,
                    minHeight: 44,
                    paddingVertical: 10,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: isPublic ? ACCENT : BORDER,
                    backgroundColor: isPublic ? surface.selected : CARD,
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900" }}>{isPublic ? "✓ " : ""}Public</Text>
                </Pressable>

                <Pressable
                  accessibilityRole="radio"
                  accessibilityLabel="Groupe privé"
                  accessibilityState={{ checked: !isPublic }}
                  onPress={() => setIsPublic(false)}
                  style={{
                    flex: 1,
                    minHeight: 44,
                    paddingVertical: 10,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: !isPublic ? ACCENT : BORDER,
                    backgroundColor: !isPublic ? surface.selected : CARD,
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900" }}>{!isPublic ? "✓ " : ""}Privé</Text>
                </Pressable>
              </View>

              <Pressable
                onPress={onCreateGroup}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel="Créer le groupe"
                accessibilityState={{ disabled: busy }}
                style={{
                  minHeight: 48,
                  backgroundColor: ACCENT,
                  padding: 14,
                  borderRadius: 14,
                  alignItems: "center",
                  opacity: busy ? 0.7 : 1,
                }}
              >
                <Text style={{ color: uiText.primary, fontSize: 16, fontWeight: "900" }}>
                  Créer le groupe
                </Text>
              </Pressable>
            </>
          )}
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
